import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag } from 'lucide-react';
import Logo from './Logo';
import AdminPanel from './Admin/AdminPanel';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const navigate = useNavigate();

  // Primary nav — shown on desktop, first few items
  const primaryNav = [
    { href: "#", label: "Shop", isShop: true },
    { href: "/mff-impact", label: "Coffee & Community" },
    { href: "/bonus-batch", label: "Bonus Batch" },
    { href: "/coffee-lab", label: "Coffee Lab" },
  ];

  // Secondary nav — only in the mobile menu
  const secondaryNav = [
    { href: "#features", label: "Why Bond", isHash: true },
    { href: "#process", label: "Process", isHash: true },
    { href: "#mission", label: "Mission", isHash: true },
    { href: "#about", label: "About", isHash: true },
    { href: "#testimonials", label: "Reviews", isHash: true },
    { href: "/festival-blend", label: "Festival Blend" },
    { href: "/local-noon", label: "Local Noon Pickup" },
    { href: "/gift-certificates", label: "Gift Certificates" },
    { href: "/rateyouroast", label: "Rate Your Roast" },
    { href: "/coffee", label: "The Coffee Project" },
    { href: "/coffee-lab", label: "Coffee Lab" },
  ];

  const allNav = [...primaryNav, ...secondaryNav];

  const handleNavClick = (item: typeof allNav[number]) => {
    setIsMenuOpen(false);

    if (item.isShop) {
      const shopButton = document.querySelector('[data-shop-trigger]') as HTMLButtonElement;
      if (shopButton) {
        shopButton.click();
      }
      return;
    }

    if (item.isHash) {
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(item.href);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      } else {
        const el = document.querySelector(item.href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    navigate(item.href);
  };

  return (
    <header className="relative z-20 bg-[#132947]">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=100')]
          opacity-60 bg-cover bg-center bg-fixed transform scale-105"
          style={{
            filter: 'contrast(1.4) brightness(1.1) saturate(1.4)',
            transition: 'transform 0.5s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-[#132947]/80" />
      </div>

      <div className="container mx-auto px-4 py-4 relative">
        <nav className="flex justify-between items-center">
          <Logo size="large" />

          {/* Desktop primary nav */}
          <div className="hidden md:flex items-center">
            <div className="flex items-center space-x-8">
              {primaryNav.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className="text-base font-medium hover:text-[#fda36b] transition-colors text-white whitespace-nowrap"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 hover:bg-[#1a3557] rounded-lg transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X size={32} className="text-[#fda36b]" />
            ) : (
              <Menu size={32} className="text-[#fda36b]" />
            )}
          </button>
        </nav>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-[#132947] z-50 pt-20 overflow-y-auto">
            <div className="flex flex-col items-center space-y-6 p-4 pb-12">
              {/* Primary items first */}
              {primaryNav.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className="text-2xl font-semibold hover:text-[#fda36b] transition-colors text-white"
                >
                  {item.label}
                </button>
              ))}

              {/* Divider */}
              <div className="w-16 h-px bg-white/20 my-2" />

              {/* Secondary items */}
              {secondaryNav.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className="text-xl font-medium text-white/70 hover:text-[#fda36b] transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <AdminPanel isOpen={isAdminOpen} onClose={() => setIsAdminOpen(false)} />
    </header>
  );
};

export default Header;
